import {
  MIN_WINDOW_MIN,
  TIME_WINDOWS,
  timeWindowCodeFor,
  windowMinutes,
  type Availability,
} from '@otryad/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useHaptic } from '@/app/TmaProvider';
import { Card, Chip, ChipGroup, Field, MainButton, TextInput } from '@/components/ui';
import { texts, weekdayLong, weekdayShort } from '@/lib/texts';

import { OnboardingScreen } from './shared';

const t = texts.onboarding.schedule;
const DAYS = [1, 2, 3, 4, 5, 6, 7] as const;
const DEFAULT_WINDOW = TIME_WINDOWS[0]!;

export interface AvailabilityEditorProps {
  value: Availability | undefined;
  onChange: (value: Availability) => void;
}

function windowLabel(code: string): string {
  const labels = texts.timeWindows as Record<string, string>;
  return labels[code] ?? code;
}

/** Дни недели + окно времени. Используется в онбординге и профиле. */
export function AvailabilityEditor({ value, onChange }: AvailabilityEditorProps) {
  const haptic = useHaptic();
  const days = value?.days ?? [];
  const from = value?.from ?? DEFAULT_WINDOW.from;
  const to = value?.to ?? DEFAULT_WINDOW.to;
  const presetCode = timeWindowCodeFor(from, to);
  const [custom, setCustom] = useState(presetCode === null && value !== undefined);

  const emit = (next: Partial<Availability>) =>
    onChange({ days: [...days], from, to, ...next } as Availability);

  const toggleDay = (d: number) => {
    haptic.selection();
    const next = days.includes(d)
      ? days.filter((x) => x !== d)
      : [...days, d].sort((a, b) => a - b);
    emit({ days: next as Availability['days'] });
  };

  return (
    <>
      <Card title={t.daysLabel}>
        <div className="grid grid-cols-7 gap-1.5">
          {DAYS.map((d) => {
            const on = days.includes(d);
            return (
              <button
                key={d}
                type="button"
                role="checkbox"
                aria-checked={on}
                aria-label={weekdayLong(d)}
                onClick={() => toggleDay(d)}
                className={
                  'rounded-control pressable press-scale flex h-12 items-center justify-center text-sm font-semibold uppercase ' +
                  (on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-fg active:brightness-110')
                }
              >
                {weekdayShort(d)}
              </button>
            );
          })}
        </div>
      </Card>

      <Card title={t.windowLabel}>
        <ChipGroup radio label={t.windowLabel}>
          {TIME_WINDOWS.map((w) => (
            <Chip
              key={w.code}
              single
              selected={!custom && presetCode === w.code}
              meta={`${w.from}–${w.to}`}
              onClick={() => {
                haptic.selection();
                setCustom(false);
                emit({ from: w.from, to: w.to });
              }}
            >
              {windowLabel(w.code)}
            </Chip>
          ))}
          <Chip
            single
            selected={custom}
            onClick={() => {
              haptic.selection();
              setCustom(true);
            }}
          >
            {texts.timeWindows.custom}
          </Chip>
        </ChipGroup>
        {custom ? (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Field label={t.customFrom}>
              <TextInput
                type="time"
                value={from}
                aria-label={t.customFromLabel}
                onChange={(e) => emit({ from: e.target.value })}
              />
            </Field>
            <Field label={t.customTo}>
              <TextInput
                type="time"
                value={to}
                aria-label={t.customToLabel}
                onChange={(e) => emit({ to: e.target.value })}
              />
            </Field>
          </div>
        ) : null}
      </Card>
    </>
  );
}

export function availabilityValid(a: Availability | undefined): a is Availability {
  // Раунд 4, N6: окно короче самого короткого сбора не принимаем — ни здесь, ни на сервере
  return Boolean(
    a &&
      a.days.length > 0 &&
      /^\d{2}:\d{2}$/.test(a.from) &&
      a.from < a.to &&
      windowMinutes(a.from, a.to) >= MIN_WINDOW_MIN,
  );
}

/** Шаг 3 — свободные вечера: дни недели + окно времени. */
export function ScheduleScreen() {
  return (
    <OnboardingScreen step={3} title={t.title} subtitle={t.subtitle}>
      {({ draft, update }) => <Body value={draft.availability} update={update} />}
    </OnboardingScreen>
  );
}

function Body({
  value,
  update,
}: {
  value: Availability | undefined;
  update: (p: { availability?: Availability; step?: number }) => void;
}) {
  const navigate = useNavigate();
  const hasDays = (value?.days.length ?? 0) > 0;
  const ordered = value !== undefined && value.from < value.to;
  const valid = availabilityValid(value);
  return (
    <>
      <AvailabilityEditor value={value} onChange={(availability) => update({ availability })} />
      {/* Подсказка называет то, что мешает: при выбранных днях и окне «23:00–18:00» она
          просила отметить день (раунд 3, N5) */}
      {!valid ? (
        <p className="text-muted px-1 text-sm">
          {!hasDays ? t.needDay : ordered ? t.shortWindow : t.needWindow}
        </p>
      ) : null}
      <MainButton
        text={texts.common.next}
        enabled={valid}
        onClick={() => {
          update({ step: 3 });
          void navigate('/onboarding/activities');
        }}
      />
    </>
  );
}
